import type { ButtonProps, TooltipProps } from 'antd';
import { Button, Select, Space, Typography, Modal, Tooltip } from 'antd';
import React, { useEffect } from 'react';
import { useStoreActions, useStoreState } from '../../store';
import type { PCTenant } from '../../store/partnerConfiguration/types';
import { PC_GLOBAL_TENANT_ID } from '../../store/partnerConfiguration/types';
import { SearchBar } from '../workflow/shared/SearchBar';
import { useConfigStyles } from './ConfigurePageStyles';
import { BaseFeatureToggles } from '../../store/navigation/BaseFeatureToggles';
const { Link } = Typography;
const { Option } = Select;

export type AssetsAction = {
  title: string;
  onClick: (record: any) => void;
  icon: JSX.Element;
  buttonProps?: ButtonProps;
  buttonTooltipProps?: TooltipProps;
};

type ActionBarProps = {
  pageActions?: AssetsAction[];
  setSearchBarValue: React.Dispatch<React.SetStateAction<string>>;
  tenantId?: string;
  setTenantId?: React.Dispatch<React.SetStateAction<string>>;
  searchPlaceholder?: string;
  searchHelp?: React.ReactNode;
  children?: React.ReactNode;
};

export const AssetsActionBar = ({
  pageActions,
  setSearchBarValue,
  tenantId,
  setTenantId,
  searchPlaceholder,
  searchHelp,
  children,
}: ActionBarProps) => {
  const classes = useConfigStyles();
  const fetchPCTenants = useStoreActions((actions) => actions.partnerConfiguration.tenants.fetchPCTenants);
  const pcTenants = useStoreState((state) => state.partnerConfiguration.tenants.pcTenants);
  const checkFetched = useStoreState((state) => state.dataFetching.checkFetched);
  const usesTenant =
    useStoreState((state) => state.partner.activePartner)?.featureToggles[BaseFeatureToggles.TENANT] === 'true';
  const activePartner = useStoreState((state) => state.partner.activePartner);
  const sessionPartnerId = useStoreState((state) => state.session.user?.sessionPartnerId);
  const fetchActivePartner = useStoreActions((actions) => actions.partner.fetchPartner);
  const [helpModal, contextHolder] = Modal.useModal();

  useEffect(() => {
    if (!activePartner && sessionPartnerId) {
      fetchActivePartner(sessionPartnerId);
    }
  }, [activePartner, fetchActivePartner, sessionPartnerId]);

  useEffect(() => {
    if (!checkFetched('fetchPCTenants')) {
      fetchPCTenants();
    }
  }, [checkFetched, fetchPCTenants, pcTenants]);

  return (
    <div className={classes.header}>
      <Space size={10}>
        <div style={{ width: '25%', minWidth: '300px' }}>
          <SearchBar setSearchBarValue={setSearchBarValue} placeholder={searchPlaceholder} />
        </div>
        {usesTenant && tenantId && setTenantId && (
          <Select
            value={tenantId}
            onChange={(value) => {
              setTenantId(value);
            }}
            style={{ width: '200px' }}
            defaultValue={PC_GLOBAL_TENANT_ID}
          >
            <Option value={PC_GLOBAL_TENANT_ID}>{'Global (All Records)'}</Option>
            {Object.values(pcTenants).map((tenant: PCTenant) => {
              return (
                <Option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </Option>
              );
            })}
          </Select>
        )}
        {searchHelp && (
          <Link
            onClick={() => {
              helpModal.info({
                content: searchHelp,
                title: 'Supported Search Terms',
              });
            }}
            style={{
              marginLeft: '20px',
              textDecoration: 'underline',
              top: '5px',
            }}
          >
            Supported Search Terms
          </Link>
        )}
        {children}
      </Space>
      <Space size={10}>
        {pageActions?.map((pageAction) =>
          pageAction.buttonTooltipProps ? (
            <Tooltip {...pageAction.buttonTooltipProps}>
              <Button type="primary" icon={pageAction.icon} onClick={pageAction.onClick} {...pageAction?.buttonProps}>
                {pageAction.title}
              </Button>
            </Tooltip>
          ) : (
            <Button type="primary" icon={pageAction.icon} onClick={pageAction.onClick} {...pageAction?.buttonProps}>
              {pageAction.title}
            </Button>
          ),
        )}
      </Space>
      {contextHolder}
    </div>
  );
};
